// @flow

/* eslint-env browser */

import * as React from 'react';

import clsx from 'clsx';
import { connect, useDispatch } from 'react-redux';
import { compose } from 'redux';

import { selectTaxesDisplaySetting } from '@gandi/authentication';
import {
  getCart,
  upsertCartProduct,
  selectCartUUID,
  selectProductsByType,
} from '@gandi/cart';
import type { ProductPayload } from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';

import {
  type TranslatorProps,
  useTranslator,
  withTranslator,
  mapTags,
} from '@gandi/translate';

import {
  Alert,
  Base,
  Button,
  Label,
  PopoverIcon,
  Spinner,
  Svg,
} from '@gandi/ui';

import {
  Form,
  type FormComponentProps,
  InputTextField,
  Fieldset,
  FormField,
  HelpMessage,
  ErrorMessage,
  getFormValues,
  SelectField,
  ToggleGroupField,
  TogglePillField,
  withForm,
  SubmissionError,
} from 'gandi.react-form';

import { withRouter } from 'src/helpers/withRouter';
import { useAppSelector } from 'src/hooks/useAppSelector';

import { Intro } from '../Layout';
import { useGetPrice } from '../helpers';
import type { WithRouterProps } from '../helpers/withRouter';
import { selectShopCurrent } from '../modules/organization/selectors';
import type { Dispatch, FullState } from '../types';

import { useTmchPrices } from './hooks/useTmchPrices';
import {
  uploadDocuments,
  fetchJurisdictions,
  jurisdictionsResource,
} from './modules/actions';
import { selectJurisdictions, selectPrices } from './modules/selector';

import validate from './validation';

import styles from './Form.scss';

type StateProps = {
  initialValues: {
    duration: string,
    holder_status: string,
  },
};

export type InputProps = {
  checked?: boolean,
  name: string,
  value: any,
  onBlur: (evt: SyntheticFocusEvent<>) => mixed,
  onChange: (evt: SyntheticInputEvent<>) => mixed,
  onDragStart: (evt: SyntheticDragEvent<>) => mixed,
  onDrop: (evt: SyntheticDragEvent<>) => mixed,
};

export type MetaProps = {
  active: boolean,
  autofilled: boolean,
  asyncValidating: boolean,
  dirty: boolean,
  dispatch: Dispatch,
  error?: string,
  invalid: boolean,
  pristine: boolean,
  submitting: boolean,
  touched: boolean,
  valid: boolean,
  visited: boolean,
  warning: boolean,
};

type FieldInputProps = {
  input: InputProps,
  meta: MetaProps,
};

type FormFieldsProps = {
  ...FieldInputProps,
  id: string,
  accept: string,
  label: string,
  submitFailed?: boolean,
  error: string,
  help?: string,
  popoverIcon: ExactReactElement_DEPRECATED<typeof PopoverIcon>,
};

type InjectedProps = {
  ...WithRouterProps<>,
  form: FormComponentProps,

  translator: TranslatorProps,
};

type Props = {
  ...InjectedProps,
  ...StateProps,
};

const formName = 'tmch-form';

type UploadSuccessProps = {
  children: string,
  onClick: (evt: SyntheticEvent<HTMLButtonElement>) => mixed,
};

const UploadSuccess = ({ children, onClick }: UploadSuccessProps) => {
  const { __ } = useTranslator();

  return (
    <Base flex flexWrap flexAlignItemsCenter className={styles.uploadSuccess}>
      <Svg name="successWire" theme="success" />
      <Base tagName="p" marginRight="xsmall">
        {children}
      </Base>
      <Button kind="link" type="button" onClick={onClick}>
        {__('Remove')}
      </Button>
    </Base>
  );
};

const TMCHForm = ({ form }: Props) => {
  const dispatch = useDispatch();
  const translator = useTranslator();
  const { __, formatPrice } = translator;
  const { prices, pricesFailure, pricesFetching, pricesLoaded } =
    useTmchPrices();
  const [checking, setChecking] = React.useState({
    declaration_proof_of_use: {
      check: false,
      name: '',
    },
    proof_of_use: {
      check: false,
      name: '',
    },
    assignee_declaration: {
      check: false,
      name: '',
    },
    licensee_declaration: {
      check: false,
      name: '',
    },
  });

  const displayTaxes = useAppSelector(selectTaxesDisplaySetting);
  const formValues = useAppSelector(getFormValues(formName));

  const jurisdictions = useAppSelector(selectJurisdictions);

  const jurisdictionsLoaded = useAppSelector(
    jurisdictionsResource.selectIsLoaded,
  );
  const jurisdictionsFailure = useAppSelector(
    jurisdictionsResource.selectIsFailure,
  );
  const jurisdictionsFetching = useAppSelector(
    jurisdictionsResource.selectIsFetching,
  );

  const isLoaded = pricesLoaded && jurisdictionsLoaded;
  const isFailure = pricesFailure || jurisdictionsFailure;
  const isFetching = pricesFetching || jurisdictionsFetching;

  const getPrice = useGetPrice();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    if (
      !jurisdictionsLoaded &&
      !jurisdictionsFailure &&
      !jurisdictionsFetching
    ) {
      dispatch(fetchJurisdictions()).then(
        response => response,
        error => {
          dispatch(
            notifyDanger(
              error.response?.data?.message || __('Unexpected error occurred'),
            ),
          );
        },
      );
    }
  }, [
    __,
    dispatch,
    jurisdictionsFailure,
    jurisdictionsFetching,
    jurisdictionsLoaded,
  ]);

  const onSelectFile = (
    evt: SyntheticInputEvent<>,
    name: string,
    input: InputProps,
  ) => {
    const file = evt.target.files[0];

    if (file && file.size >= 1024 * 1024) {
      // $FlowFixMe[incompatible-call]
      input.onChange(file);
    } else if (file && 'FormData' in window) {
      const data = new FormData();
      data.set('document', file, file.name);

      setChecking(prevState => ({
        ...prevState,
        [name]: {
          check: true,
          name: file.name,
        },
      }));

      // $FlowFixMe[class-object-subtyping]
      dispatch(uploadDocuments(data, name)).then(resp =>
        // $FlowFixMe[incompatible-use]
        // $FlowFixMe[prop-missing]
        input.onChange(resp.data.id.toString()),
      );
    }
  };

  const renderFieldUpload = (props: FormFieldsProps) => {
    const showError =
      !!props.meta.error && (props.meta.dirty || props.submitFailed);

    return (
      <Fieldset className={styles.fieldUpload} error={showError}>
        <Label
          target={props.input.name}
          required
          popoverIcon={props.popoverIcon}
        >
          {props.label}
        </Label>
        {
          // $FlowFixMe[invalid-computed-prop]
          checking[props.input.name].check && (
            <UploadSuccess
              onClick={() =>
                setChecking(prevState => ({
                  ...prevState,
                  [props.input.name]: {
                    check: false,
                    name: '',
                  },
                }))
              }
            >
              {checking[props.input.name].name}
            </UploadSuccess>
          )
        }
        {/* eslint-disable-next-line @gandi/gandi/no-global-element */}
        <input
          id={props.id}
          name={props.input.name}
          onChange={e => onSelectFile(e, props.input.name, props.input)}
          className="form-control"
          type="file"
          accept={props.accept}
          // $FlowFixMe[invalid-computed-prop]
          {...(checking[props.input.name].check && {
            style: { display: 'none' },
          })}
        />

        {showError && <ErrorMessage text={props.meta.error || props.error} />}
        {props.meta.warning && <HelpMessage text={props.meta.warning} />}
        {props.help && <HelpMessage text={props.help} />}
      </Fieldset>
    );
  };

  const renderPrice = () => {
    const priceBase = prices.find(e => e.min_duration === 1);
    const priceRef = priceBase && getPrice(priceBase);
    const currentPrice = prices.find(
      price => price.min_duration.toString() === formValues?.duration,
    );

    const price =
      !!currentPrice &&
      formatPrice(
        getPrice(currentPrice) * currentPrice.min_duration,
        currentPrice.currency,
      );

    return (
      <>
        {currentPrice && currentPrice.min_duration !== 1 && priceRef ? (
          <>
            <Base color="base" tagName="del">
              {formatPrice(
                priceRef * currentPrice.min_duration,
                currentPrice.currency,
              )}
            </Base>{' '}
            <Base color="base" tagName="span" marginLeft="xsmall">
              {price}
            </Base>
          </>
        ) : (
          <Base color="base" tagName="span">
            {price}
          </Base>
        )}
        {currentPrice && currentPrice.taxes.length > 0 && (
          <Base tagName="span" marginLeft="xsmall" color="light">
            {displayTaxes ? __('incl. taxes') : __('excl. taxes')}
          </Base>
        )}
      </>
    );
  };

  const renderPopoverIcon = (id: string, text: string) => (
    <PopoverIcon
      id={`popover-${id}`}
      title={translator.__('Informations')}
      icon="questionWire"
    >
      {id === 'email'
        ? mapTags(text, ({ link }) => ({
            link: (
              <a
                href={__(
                  'https://docs.gandi.net/en/domain_names/domain_services/tmch.html',
                )}
                target="_blank"
                rel="noopener noreferrer"
              >
                {link}
              </a>
            ),
          }))
        : text}
    </PopoverIcon>
  );

  return (
    <>
      <Intro title={__('TMCH Trademark Registration')} />

      {isFailure && !isFetching && (
        <Alert
          title={__('Unexpected error')}
          message={__('Unexpected error occurred. Please try again later.')}
          intent="danger"
        />
      )}

      {!isLoaded && !isFailure && isFetching && <Spinner centered />}

      {isLoaded && (
        <>
          <Form
            action=""
            className={clsx('form-stacked', styles.form)}
            id={form.form}
            onSubmit={form.handleSubmit}
          >
            <ToggleGroupField
              legend={__('Type')}
              name="organization_type"
              required
            >
              <TogglePillField value="company">{__('Company')}</TogglePillField>
              <TogglePillField value="publicbody">
                {__('Public Body')}
              </TogglePillField>
              <TogglePillField value="association">
                {__('Association')}
              </TogglePillField>
              <TogglePillField value="individual">
                {__('Individual')}
              </TogglePillField>
            </ToggleGroupField>

            <InputTextField
              placeholder={__('Please enter your exact registered trademark.')}
              label={__('Trademark')}
              name="mark_name"
              popoverIcon={renderPopoverIcon(
                'mark_name',
                __(
                  'The name of the mark needs to be an exact match with the registered trademark. ' +
                    'If the name contains accents, spaces or special characters these need to be included.',
                ),
              )}
              required
            />
            <InputTextField
              label={__('Brand Registration Number')}
              name="registration_number"
              required
            />

            <SelectField
              placeholder={__('Select jurisdiction')}
              label={__('Jurisdiction')}
              values={jurisdictions}
              required
              name="jurisdiction"
              popoverIcon={renderPopoverIcon(
                'jurisdiction',
                __('Jurisdiction where the trademark has been registered.'),
              )}
              variant={['block']}
            />

            <ToggleGroupField
              legend={__('Status of the filer')}
              name="holder_status"
              defaultValue="owner"
              required
            >
              <TogglePillField value="owner">{__('Owner')}</TogglePillField>
              <TogglePillField value="assignee">
                {__('Assignee')}
              </TogglePillField>
              <TogglePillField value="licensee">
                {__('Licensee')}
              </TogglePillField>
            </ToggleGroupField>

            {formValues?.organization_type === 'individual' ? (
              <>
                <InputTextField
                  label={__('First Name of the filer')}
                  name="firstname"
                  required
                />
                <InputTextField
                  label={__('Last name of the filer')}
                  name="lastname"
                  required
                />
              </>
            ) : (
              <InputTextField
                label={__('Filing Company')}
                name="orgname"
                required
              />
            )}

            <InputTextField
              label={__('TMCH contact email')}
              name="email"
              popoverIcon={renderPopoverIcon(
                'email',
                __(
                  'This is the email address on which you will be notified by ' +
                    'the TMCH when someone registers your trademark in a supported ' +
                    'extension. [[link:Learn more]]',
                ),
              )}
              required
              type="email"
            />

            <FormField
              name="declaration_proof_of_use"
              help={
                <a
                  // XXX: Put the url in conf?
                  href="https://www.trademark-clearinghouse.com/sites/default/files/files/downloads/declaration_of_proof_of_use_-_tm_holder.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                >
                  {__('Download Declaration of proof of use template')}
                </a>
              }
              label={__('Declaration of proof of use')}
              component={renderFieldUpload}
              accept=".pdf"
              popoverIcon={renderPopoverIcon(
                'declaration_proof_of_use',
                __(
                  'Fill in the Declaration of proof of use you have downloaded, and then upload it here in PDF format.',
                ),
              )}
            />

            <FormField
              name="proof_of_use"
              help={__(
                'Usually a website screenshot of the exact brand. Jpg, png, or pdf, smaller than 1 MB.',
              )}
              label={__('Proof of use')}
              component={renderFieldUpload}
              accept="image/png, image/jpeg, .pdf"
              popoverIcon={renderPopoverIcon(
                'proof_of_use',
                __(
                  'Upload a document proving you are using the trademark, such as a screenshot from your website. ' +
                    'The trademark on the document must be an exact match with the trademark you want to register at the TMCH. ' +
                    'This document must be in JPG, PNG, or PDF format.',
                ),
              )}
            />

            {formValues?.holder_status === 'assignee' && (
              <FormField
                name="assignee_declaration"
                help={__('Must be completed and signed.')}
                label={__('Assignee declaration')}
                component={renderFieldUpload}
                accept="image/jpeg, .pdf"
                popoverIcon={renderPopoverIcon(
                  'assignee_declaration',
                  __('Upload your Assignee declaration in JPG or PDF format.'),
                )}
              />
            )}

            {formValues?.holder_status === 'licensee' && (
              <FormField
                name="licensee_declaration"
                help={__('Must be completed and signed.')}
                label={__('Licensee declaration')}
                component={renderFieldUpload}
                accept="image/jpeg, .pdf"
                popoverIcon={renderPopoverIcon(
                  'licensee_declaration',
                  __('Upload your Licensee declaration in JPG or PDF format.'),
                )}
              />
            )}

            {Array.isArray(prices) && prices.length > 0 && (
              <ToggleGroupField
                name="duration"
                legend={__('TMCH registration duration')}
                required
                help={renderPrice()}
              >
                {prices.map(price => (
                  <TogglePillField
                    key={`${price.min_duration}${price.duration_unit}`}
                    value={`${price.min_duration}`}
                  >
                    {__('%(count)s years', { count: price.min_duration })}
                  </TogglePillField>
                ))}
              </ToggleGroupField>
            )}
          </Form>

          <Base padding="small" marginBottom="base" backgroundColor="surface">
            <Base tagName="p" marginTop="none" marginBottom="none">
              {mapTags(
                __(
                  'TMCH will automatically protect some variations of your trademark name at no additional cost. [[link:Learn more]]',
                ),
                ({ link }) => ({
                  link: (
                    <a
                      href={__(
                        'https://docs.gandi.net/en/domain_names/domain_services/tmch.html',
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {link}
                    </a>
                  ),
                }),
              )}
            </Base>
          </Base>

          <Button
            grouped
            type="submit"
            form={form.form}
            disabled={!form.valid || form.submitting}
          >
            {__('Continue')}
          </Button>
        </>
      )}
    </>
  );
};

const onSubmit = (values: ProductPayload, dispatch: Dispatch, props: Props) =>
  dispatch((_: any, getState: () => FullState) => {
    const {
      translator: { __ },
    } = props;

    const product_type = 'tmch';

    const productId = selectProductsByType(getState(), {
      productType: product_type,
    })[0]?.id;

    const productData = {
      product_type: 'tmch',
      process: 'create',
      payload: {
        ...values,
        duration_unit: 'year',
        duration: parseInt(values.duration, 10),
        label: __('Tmch'),
      },
    };

    return dispatch(upsertCartProduct({ productData, productId })).then(
      response => {
        const cartId = selectCartUUID(getState());
        let errors;
        const valuesForm = getFormValues(formName)(getState());
        const fieldsNames = Object.keys(valuesForm);

        // Refetch the cart after cart modifications (product insertion or update)
        dispatch(getCart(cartId));

        if (response.data?.errors) {
          // Retrieve all field names in errors
          const errorsName = response.data.errors.map(error => error.name);
          // find out if there are any errors in the form
          const isFormErrors = errorsName.some(error =>
            fieldsNames.includes(error),
          );
          errors = response.data.errors.reduce((acc, error) => {
            if (fieldsNames.includes(error.name)) {
              acc[error.name] = error.description;
              // If there are errors in the form do not display errors that do not relate to the form
            } else if (!isFormErrors) {
              dispatch(notifyDanger(error.description));
            }
            return acc;
          }, {});

          if (errors) {
            throw new SubmissionError(errors);
          }
        } else if (!cartId) {
          props.navigate(`/cart/overview`);
        } else {
          props.navigate(`/cart/${cartId}/overview`);
        }
      },
      error =>
        dispatch(
          notifyDanger(
            error.response?.data?.message || __('Unexpected error occurred'),
          ),
        ),
    );
  });

const mapStateToProps = (state: FullState): StateProps => {
  const organizationDetails = selectShopCurrent(state, true);

  // same as in useTmchPrices
  const prices = selectPrices(state);

  // $FlowFixMe[prop-missing]
  return {
    initialValues: {
      duration:
        Array.isArray(prices) && prices[0]
          ? prices[0].min_duration.toString()
          : '',
      email: organizationDetails?.email,
      holder_status: 'owner',
    },
  };
};

export default (compose(
  withTranslator({ propsNamespace: 'translator' }),
  withRouter,
  connect(mapStateToProps),
  withForm({
    propNamespace: 'form',
    form: formName,
    validate,
    onSubmit,
  }),
)(TMCHForm): React.ComponentType<{}>);
